// Calendar & Scheduling · Package 1 — Project Schedule Summary Card.
//
// Mounted in the Project Workspace (ProjectDetail.jsx). Surfaces the
// next scheduled work + upcoming list, and an inline create/edit
// dialog. Uses the existing shadcn/ui Dialog + AlertDialog primitives,
// the sonner toast pattern, and the shared `useLang().t` translator so
// every contractor-facing string participates in English / Spanish.
//
// Conflict detection is a SOFT WARNING surface only — the contractor
// always makes the final decision. The dialog never prevents save.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarClock, Plus, Users, Pencil, Trash2, Copy, Loader2,
  AlertTriangle, RefreshCw, X,
} from "lucide-react";

const ASSIGNEE_KINDS = ["self", "individual", "crew", "subcontractor"];
const RECURRENCE_RULES = ["none", "daily", "weekly", "monthly"];
const STATUSES = ["scheduled", "in_progress", "completed", "canceled"];

// ---------- Date-time input helpers ----------
// Convert an ISO 8601 string (possibly Z-suffixed) into a
// `YYYY-MM-DDTHH:mm` string for a `<input type="datetime-local">`
// bound to LOCAL time. The reverse converts the local input back to an
// ISO string via the browser's local zone before we send to the API.

const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
};

const isoToDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const dateInputToIso = (dateStr, endOfDay = false) => {
  if (!dateStr) return "";
  // Interpret the date at LOCAL midnight (or 23:59:59.999 for end-of-day).
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
  return dt.toISOString();
};

const fmtDateTime = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const locale = lang === "es" ? "es-ES" : "en-US";
  return d.toLocaleString(locale, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const emptyDraft = (projectId) => ({
  project_id: projectId, title: "", description: "",
  assignee_id: "", status: "scheduled",
  start_at: "", end_at: "", all_day: false,
  recurrence: { rule: "none", interval: 1, count: null, until: null },
  internal_notes: "", customer_notes: "",
  reminder: { minutes_before: null },
});

// ---------- Component ----------

const ProjectScheduleCard = ({ projectId }) => {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // event object when editing
  const [draft, setDraft] = useState(emptyDraft(projectId));
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [evR, asR] = await Promise.all([
        api.get(`/projects/${projectId}/schedule-events`),
        api.get("/assignees"),
      ]);
      setEvents(evR.data || []);
      setAssignees(asR.data || []);
    } catch (err) {
      // Non-fatal — an empty card renders and the contractor can retry.
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft(projectId));
    setConflicts([]);
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setDraft({
      project_id: projectId,
      title: ev.title || "",
      description: ev.description || "",
      assignee_id: ev.assignee_id || "",
      status: ev.status || "scheduled",
      start_at: ev.start_at || "",
      end_at: ev.end_at || "",
      all_day: !!ev.all_day,
      recurrence: ev.recurrence || { rule: "none", interval: 1, count: null, until: null },
      internal_notes: ev.internal_notes || "",
      customer_notes: ev.customer_notes || "",
      reminder: ev.reminder || { minutes_before: null },
    });
    setConflicts([]);
    setDialogOpen(true);
  };

  // Live conflict check — fires whenever the contractor picks bounds
  // and (optionally) an assignee. Advisory only.
  useEffect(() => {
    if (!dialogOpen) return;
    if (!draft.start_at || !draft.end_at) { setConflicts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/schedule-events/check-conflicts", {
          project_id: projectId,
          start_at: draft.start_at,
          end_at: draft.end_at,
          all_day: !!draft.all_day,
          assignee_id: draft.assignee_id || "",
          recurrence: draft.recurrence || { rule: "none", interval: 1 },
          exclude_event_id: editing?.id || "",
        });
        if (!cancelled) setConflicts(data?.conflicts || []);
      } catch (_) {
        if (!cancelled) setConflicts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [dialogOpen, draft.start_at, draft.end_at, draft.assignee_id,
      draft.all_day, draft.recurrence, editing, projectId]);

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error(t("schedule.errors.title_required"));
      return;
    }
    if (!draft.start_at || !draft.end_at) {
      toast.error(t("schedule.errors.times_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        title: draft.title.trim(),
        description: draft.description || "",
        assignee_id: draft.assignee_id || "",
        status: draft.status,
        start_at: draft.start_at,
        end_at: draft.end_at,
        all_day: !!draft.all_day,
        recurrence: draft.recurrence,
        internal_notes: draft.internal_notes || "",
        customer_notes: draft.customer_notes || "",
        reminder: draft.reminder,
      };
      if (editing) {
        await api.put(`/schedule-events/${editing.id}`, payload);
        toast.success(t("schedule.toast_updated"));
      } else {
        await api.post(`/projects/${projectId}/schedule-events`, payload);
        toast.success(t("schedule.toast_created"));
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.error || t("schedule.toast_save_failed"));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/schedule-events/${deleteTarget.id}`);
      toast.success(t("schedule.toast_deleted"));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("schedule.toast_delete_failed"));
    }
  };

  const duplicate = async (ev) => {
    try {
      await api.post(`/schedule-events/${ev.id}/duplicate`);
      toast.success(t("schedule.toast_duplicated"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("schedule.toast_duplicate_failed"));
    }
  };

  // Split upcoming vs past.
  const now = Date.now();
  const upcoming = useMemo(() => (events || []).filter((e) => {
    const end = new Date(e.end_at || 0).getTime();
    return isNaN(end) || end >= now;
  }), [events, now]);
  const nextEvent = upcoming[0] || null;
  const rest = upcoming.slice(1, 6);

  return (
    <section
      data-testid="project-schedule-card"
      className="jc-card p-5 mb-6"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-[var(--jc-text-soft)]" />
          <div className="overline">{t("schedule.card_overline")}</div>
        </div>
        <button
          onClick={openCreate}
          data-testid="schedule-add-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
        >
          <Plus size={14} /> {t("schedule.add_btn")}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="schedule-loading">
          {t("schedule.loading")}
        </div>
      ) : nextEvent ? (
        <>
          <div
            data-testid="schedule-next"
            className="rounded-md border border-[var(--jc-border)] p-3 mb-3 bg-white flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2"
          >
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-widest uppercase text-[var(--jc-text-soft)] mb-1">
                {t("schedule.next_up")}
              </div>
              <div className="font-display text-base truncate" data-testid="schedule-next-title">
                {nextEvent.title}
              </div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
                {fmtDateTime(nextEvent.start_at, lang)} → {fmtDateTime(nextEvent.end_at, lang)}
              </div>
              <div className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1">
                  <Users size={12} className="text-[var(--jc-text-soft)]" />
                  {nextEvent.assignee?.display_name || t("schedule.unassigned")}
                </span>
                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[var(--jc-bg)] border border-[var(--jc-border)] text-[var(--jc-text-soft)]">
                  {t(`schedule.status.${nextEvent.status || "scheduled"}`)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => openEdit(nextEvent)}
                data-testid={`schedule-edit-btn-${nextEvent.id}`}
                className="p-1.5 rounded-md border border-[var(--jc-border)] text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
                title={t("schedule.edit")}
              >
                <Pencil size={13} />
              </button>
              <button
                onClick={() => duplicate(nextEvent)}
                data-testid={`schedule-duplicate-btn-${nextEvent.id}`}
                className="p-1.5 rounded-md border border-[var(--jc-border)] text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
                title={t("schedule.duplicate")}
              >
                <Copy size={13} />
              </button>
              <button
                onClick={() => setDeleteTarget(nextEvent)}
                data-testid={`schedule-delete-btn-${nextEvent.id}`}
                className="p-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                title={t("schedule.delete")}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          {rest.length > 0 && (
            <>
              <div className="overline mb-1.5">{t("schedule.upcoming")}</div>
              <ul data-testid="schedule-upcoming-list" className="divide-y divide-[var(--jc-border)]">
                {rest.map((e) => (
                  <li key={e.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{e.title}</div>
                      <div className="text-xs text-[var(--jc-text-soft)]">
                        {fmtDateTime(e.start_at, lang)} · {e.assignee?.display_name || t("schedule.unassigned")}
                      </div>
                    </div>
                    <button
                      onClick={() => openEdit(e)}
                      data-testid={`schedule-edit-btn-${e.id}`}
                      className="text-xs font-semibold text-[var(--jc-blue)] hover:underline shrink-0"
                    >
                      {t("schedule.edit")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      ) : (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="schedule-empty">
          {t("schedule.empty_state")}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="schedule-dialog"
          className="max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {editing ? t("schedule.dialog_edit_title") : t("schedule.dialog_create_title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Field label={t("schedule.field_title")}>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="schedule-field-title"
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                placeholder={t("schedule.title_placeholder")}
              />
            </Field>

            <Field label={t("schedule.field_assignee")}>
              <select
                value={draft.assignee_id}
                onChange={(e) => setDraft({ ...draft, assignee_id: e.target.value })}
                data-testid="schedule-field-assignee"
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
              >
                <option value="">{t("schedule.assignee_none")}</option>
                {assignees.filter((a) => a.active !== false).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name} · {t(`schedule.assignee_kind.${a.kind}`)}
                  </option>
                ))}
              </select>
              <AddAssigneeInline onCreated={(a) => {
                setAssignees((prev) => [...prev, a]);
                setDraft((d) => ({ ...d, assignee_id: a.id }));
              }} />
            </Field>

            <div className="flex items-center gap-2">
              <input
                id="schedule-all-day"
                type="checkbox"
                checked={!!draft.all_day}
                onChange={(e) => {
                  const allDay = e.target.checked;
                  setDraft((d) => {
                    if (allDay && d.start_at) {
                      return {
                        ...d,
                        all_day: true,
                        start_at: dateInputToIso(isoToDateInput(d.start_at), false),
                        end_at: dateInputToIso(isoToDateInput(d.end_at || d.start_at), true),
                      };
                    }
                    return { ...d, all_day: allDay };
                  });
                }}
                data-testid="schedule-field-all-day"
              />
              <label htmlFor="schedule-all-day" className="text-sm">
                {t("schedule.field_all_day")}
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("schedule.field_start")}>
                {draft.all_day ? (
                  <input
                    type="date"
                    value={isoToDateInput(draft.start_at)}
                    onChange={(e) => setDraft({ ...draft, start_at: dateInputToIso(e.target.value, false) })}
                    data-testid="schedule-field-start"
                    className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                  />
                ) : (
                  <input
                    type="datetime-local"
                    value={isoToLocalInput(draft.start_at)}
                    onChange={(e) => setDraft({ ...draft, start_at: localInputToIso(e.target.value) })}
                    data-testid="schedule-field-start"
                    className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                  />
                )}
              </Field>
              <Field label={t("schedule.field_end")}>
                {draft.all_day ? (
                  <input
                    type="date"
                    value={isoToDateInput(draft.end_at)}
                    onChange={(e) => setDraft({ ...draft, end_at: dateInputToIso(e.target.value, true) })}
                    data-testid="schedule-field-end"
                    className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                  />
                ) : (
                  <input
                    type="datetime-local"
                    value={isoToLocalInput(draft.end_at)}
                    onChange={(e) => setDraft({ ...draft, end_at: localInputToIso(e.target.value) })}
                    data-testid="schedule-field-end"
                    className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                  />
                )}
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("schedule.field_recurrence")}>
                <select
                  value={draft.recurrence?.rule || "none"}
                  onChange={(e) => setDraft({
                    ...draft,
                    recurrence: {
                      ...(draft.recurrence || {}),
                      rule: e.target.value,
                      interval: 1,
                    },
                  })}
                  data-testid="schedule-field-recurrence"
                  className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
                >
                  {RECURRENCE_RULES.map((r) => (
                    <option key={r} value={r}>{t(`schedule.recurrence_rules.${r}`)}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("schedule.field_status")}>
                <select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  data-testid="schedule-field-status"
                  className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{t(`schedule.status.${s}`)}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label={t("schedule.field_reminder_minutes")}>
              <input
                type="number"
                min="0"
                value={draft.reminder?.minutes_before ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft({
                    ...draft,
                    reminder: { minutes_before: v === "" ? null : Math.max(0, Number(v)) },
                  });
                }}
                data-testid="schedule-field-reminder"
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                placeholder={t("schedule.reminder_placeholder")}
              />
            </Field>

            <Field label={t("schedule.field_internal_notes")}>
              <textarea
                rows={2}
                value={draft.internal_notes}
                onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })}
                data-testid="schedule-field-internal-notes"
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
              />
            </Field>

            <Field label={t("schedule.field_customer_notes")}>
              <textarea
                rows={2}
                value={draft.customer_notes}
                onChange={(e) => setDraft({ ...draft, customer_notes: e.target.value })}
                data-testid="schedule-field-customer-notes"
                className="w-full px-3 py-2 rounded-md border border-[var(--jc-border-strong)] text-sm"
                placeholder={t("schedule.customer_notes_placeholder")}
              />
            </Field>

            {conflicts.length > 0 && (
              <div
                data-testid="schedule-conflict-warnings"
                className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-700" />
                  <div className="min-w-0">
                    <div className="font-semibold mb-1">
                      {t("schedule.conflict_heading")}
                    </div>
                    <ul className="space-y-1">
                      {conflicts.map((c) => (
                        <li key={c.event_id + c.kind} data-testid={`schedule-conflict-${c.event_id}`}>
                          <span className="font-medium">{c.title || t("schedule.conflict_unnamed")}</span>
                          {" — "}
                          <span className="text-xs">{fmtDateTime(c.start_at, lang)}</span>
                          <span className="ml-1 text-xs italic">
                            ({t(`schedule.conflict_kind.${c.kind}`)})
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs mt-1.5 opacity-80">
                      {t("schedule.conflict_never_blocks")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="schedule-cancel-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
            >
              <X size={14} /> {t("schedule.cancel")}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="schedule-save-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {saving
                ? <><Loader2 className="animate-spin" size={14} /> {t("schedule.saving")}</>
                : <><RefreshCw size={14} /> {editing ? t("schedule.save_edit") : t("schedule.save_create")}</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="schedule-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("schedule.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("schedule.delete_body", deleteTarget?.title || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="schedule-delete-cancel-btn">
              {t("schedule.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="schedule-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              {t("schedule.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const AddAssigneeInline = ({ onCreated }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("crew");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return;
    setBusy(true);
    try {
      const { data } = await api.post("/assignees", { kind, display_name: clean });
      onCreated?.(data);
      setName("");
      setOpen(false);
      toast.success(t("schedule.toast_assignee_created"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.error || t("schedule.toast_assignee_failed"));
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="schedule-add-assignee-toggle"
        className="mt-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:underline"
      >
        + {t("schedule.add_assignee")}
      </button>
    );
  }
  return (
    <div className="mt-2 flex flex-col sm:flex-row gap-2" data-testid="schedule-add-assignee-inline">
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        data-testid="schedule-add-assignee-kind"
        className="px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
      >
        {ASSIGNEE_KINDS.filter((k) => k !== "self").map((k) => (
          <option key={k} value={k}>{t(`schedule.assignee_kind.${k}`)}</option>
        ))}
      </select>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="schedule-add-assignee-name"
        placeholder={t("schedule.assignee_name_placeholder")}
        className="flex-1 px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy}
        data-testid="schedule-add-assignee-submit"
        className="px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
      >
        {busy ? "…" : t("schedule.assignee_save")}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={busy}
        data-testid="schedule-add-assignee-cancel"
        className="px-2 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)]"
      >
        {t("schedule.cancel")}
      </button>
    </div>
  );
};

export default ProjectScheduleCard;
